import { useEffect, useState } from "react";
import axios from "axios";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { toast } from "sonner";
import { API } from "@/auth/AuthContext";

export default function PayPalCheckout({ items, form, onPaid }) {
  const [config, setConfig] = useState(null);

  useEffect(() => {
    axios.get(`${API}/paypal/config`)
      .then((r) => setConfig(r.data))
      .catch(() => setConfig({ enabled: false }));
  }, []);

  if (!config) return null;

  if (!config.enabled) {
    return (
      <p className="text-[10px] text-zinc-500 font-mono text-center py-2" data-testid="paypal-unavailable">
        Online payment is being activated — send the booking request above and we'll confirm by phone.
      </p>
    );
  }

  const payload = () => ({
    name: form.name,
    contact: form.contact,
    details: form.details,
    location: form.location || "",
    preferred_date: form.preferred_date || "",
    items: items.map((i) => ({ key: i.key, name: i.name, qty: i.qty })),
  });

  return (
    <div data-testid="paypal-checkout">
      <div className="flex items-center gap-3 my-3">
        <div className="h-px flex-1 gold-line"></div>
        <span className="text-[9px] font-mono uppercase tracking-widest text-zinc-500">
          or pay now{config.environment === "sandbox" ? " · sandbox" : ""}
        </span>
        <div className="h-px flex-1 gold-line"></div>
      </div>
      <PayPalScriptProvider options={{ clientId: config.client_id, currency: "USD", intent: "capture" }}>
        <PayPalButtons
          style={{ layout: "vertical", color: "gold", shape: "rect", label: "paypal", height: 40 }}
          forceReRender={[items]}
          createOrder={async () => {
            if (!form.name || !form.contact) {
              toast.error("Enter your name and phone or email above first — then tap PayPal again.");
              throw new Error("missing contact details");
            }
            try {
              const { data } = await axios.post(`${API}/paypal/create-order`, payload());
              return data.order_id;
            } catch (err) {
              const detail = err.response?.data?.detail;
              toast.error(typeof detail === "string" ? detail : "Could not start PayPal checkout.");
              throw err;
            }
          }}
          onApprove={async (data) => {
            try {
              const { data: result } = await axios.post(`${API}/paypal/capture-order`, { order_id: data.orderID });
              if (result.status === "paid") onPaid(result);
            } catch (err) {
              const detail = err.response?.data?.detail;
              toast.error(typeof detail === "string" ? detail : "Payment could not be confirmed.");
            }
          }}
          onError={() => toast.error("PayPal checkout error. You can still send the booking request.")}
        />
      </PayPalScriptProvider>
      {(!form.name || !form.contact) && (
        <p className="text-[10px] text-zinc-500 font-mono text-center mt-2">Enter your name and contact above, then pay instantly.</p>
      )}
    </div>
  );
}
